import { useMutation, useQuery } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { groupBy } from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DemandCasesReleaseQuery, TsrctNegotiationCaseStatus } from '@/generated/gql/graphql';

import { DEMAND_CASES_RELEASE_MUTATION } from './DemandCasesReleaseMutation.gql';
import { DEMAND_CASES_RELEASE_QUERY } from './DemandCasesReleaseQuery.gql';

type NegotiationCaseNodes = DemandCasesReleaseQuery['tesseract']['demand']['negotiationCases']['nodes'];

export interface UseDemandCasesBatchReleaseDialogProps {
  demandId?: string;
  open?: boolean;
  onClose: () => void;
}

export interface UseDemandCasesBatchReleaseDialogResult {
  loading: boolean;
  casesReleasable: NegotiationCaseNodes;
  casesNotReleasable: NegotiationCaseNodes;
  selectedTotal: number;
  isSelected: (id: string) => boolean;
  onSelect: (id: string) => void;
  onRelease: () => void;
}
export const useDemandCasesBatchReleaseDialog = ({
  demandId,
  open,
  onClose,
}: UseDemandCasesBatchReleaseDialogProps): UseDemandCasesBatchReleaseDialogResult => {
  const { onError } = useCustomSnackbar();

  const [selectedMap, setSelectedMap] = useState<Record<string, boolean>>({});

  const selectedTotal = useMemo(() => {
    return Object.keys(selectedMap).reduce((acc, key) => acc + (selectedMap[key] ? 1 : 0), 0);
  }, [selectedMap]);

  const { data, loading: querying } = useQuery(DEMAND_CASES_RELEASE_QUERY, {
    variables: {
      id: demandId ?? '',
    },
    fetchPolicy: 'cache-and-network',
    skip: !open || !demandId,
    onError,
  });

  const [release, { loading: saving }] = useMutation(DEMAND_CASES_RELEASE_MUTATION, {
    onError,
  });

  const loading = querying || saving;
  const casesAll = data?.tesseract.demand?.negotiationCases.nodes;

  // only use cases with status INITIALIZED
  const casesInitialized: NegotiationCaseNodes = useMemo(() => {
    return (casesAll ?? []).filter((row) => row.status === TsrctNegotiationCaseStatus.INITIALIZED);
  }, [casesAll]);

  // select all releasable cases by default
  useEffect(() => {
    if (casesInitialized.length) {
      setSelectedMap(
        casesInitialized.reduce(
          (acc, item) => {
            acc[item.id] = item.isReleasable;
            return acc;
          },
          {} as Record<string, boolean>,
        ),
      );
    }
  }, [casesInitialized]);

  const casesGrouped = useMemo(() => {
    const result = groupBy((negotiationCase) => {
      return negotiationCase.isReleasable ? 'casesReleasable' : 'casesNotReleasable';
    }, casesInitialized);
    return {
      casesReleasable: result.casesReleasable ?? [],
      casesNotReleasable: result.casesNotReleasable ?? [],
    };
  }, [casesInitialized]);

  const onSelect: UseDemandCasesBatchReleaseDialogResult['onSelect'] = useCallback((id) => {
    setSelectedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const onRelease = () => {
    const negotiationCaseIds = Object.keys(selectedMap).reduce((acc, key) => {
      if (selectedMap[key]) {
        acc.push(key);
      }
      return acc;
    }, [] as string[]);

    (async () => {
      const { errors } = await release({ variables: { id: demandId, data: { negotiationCaseIds } } });
      if (!errors || errors.length === 0) {
        onClose();
      }
    })();
  };

  return {
    ...casesGrouped,
    loading,
    selectedTotal,
    isSelected: (id) => selectedMap[id] ?? false,
    onSelect,
    onRelease,
  };
};
